import { lazy, Suspense } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { describeTool } from "../lib/agui";

// Recharts is heavy and only some answers plot anything, so load it on demand.
// The Suspense fallback matches Chart's own "still streaming" placeholder.
const Chart = lazy(() => import("./Chart"));
const ChartFallback = <p className="text-xs text-ink-faint py-2">Preparing chart…</p>;

/**
 * Route fenced ```chart blocks to the Chart renderer, leave everything else as
 * ordinary markdown. The agent emits a chart spec as a code block so it travels
 * in the same streamed text as the prose — no separate channel, no change to the
 * event pipeline. The `pre` override strips the monospace wrapper react-markdown
 * would otherwise put around the block.
 */
const CHART_LANG = /language-chart/;

const markdownComponents: Components = {
  code({ className, children, ...rest }) {
    if (className && CHART_LANG.test(className)) {
      return (
        <Suspense fallback={ChartFallback}>
          <Chart source={String(children)} />
        </Suspense>
      );
    }
    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  pre({ children, ...rest }) {
    // react-markdown wraps a fenced block as <pre><code class="language-…">.
    // When that code is a chart, drop the <pre> chrome and let Chart stand alone.
    // `children` may be the code element directly or a single-item array.
    const child = (Array.isArray(children) ? children[0] : children) as
      | { props?: { className?: string } }
      | undefined;
    if (child?.props?.className && CHART_LANG.test(child.props.className)) {
      return <>{children}</>;
    }
    return <pre {...rest}>{children}</pre>;
  },
};

/**
 * Placeholder shown from the instant a question is sent until the first token of
 * the answer arrives. That gap is several seconds — the runtime authorises the
 * token, builds the agent, loads memory and makes a first model call before any
 * text exists — and with nothing on screen the app reads as stuck rather than
 * working. Deliberately the familiar three-dot chat idiom, in the same bubble the
 * answer will occupy, so the answer appears to arrive *in* it.
 */
function Thinking() {
  return (
    <div
      role="status"
      className="w-fit rounded-2xl rounded-bl-sm bg-white border border-line px-4 py-3.5 flex items-center gap-1.5"
    >
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-1.5 w-1.5 rounded-full bg-gold animate-thinking"
          style={{ animationDelay: `${delay}ms` }}
          aria-hidden
        />
      ))}
      <span className="sr-only">Thinking…</span>
    </div>
  );
}

export interface ToolActivity {
  id: string;
  name: string;
  /** Undefined while the call is still running. */
  finished?: boolean;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Tools the agent used to produce this answer, in order. */
  tools: ToolActivity[];
  /** Set when the agent's tool boundary blocked a call, so it is visible not silent. */
  blocked?: string[];
  error?: string;
}

export default function Message({
  message,
  pending = false,
}: {
  message: ChatMessage;
  /** This is the turn currently in flight: show progress until text arrives. */
  pending?: boolean;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-ink text-white px-4 py-2.5 text-sm">
          {message.text}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Tool activity, shown above the answer. For an assistant whose value is
          being grounded rather than plausible, showing which source was consulted
          is part of the product, not decoration. */}
      {message.tools.length > 0 && (
        <ul className="space-y-1">
          {message.tools.map((tool) => (
            <li
              key={tool.id}
              className="flex items-center gap-2 text-xs text-ink-muted"
            >
              <span
                className={
                  tool.finished
                    ? "h-1.5 w-1.5 rounded-full bg-teal"
                    : "h-1.5 w-1.5 rounded-full bg-gold animate-pulse"
                }
                aria-hidden
              />
              <span>{describeTool(tool.name)}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Kept up while tools run, so there is one continuous sign of life from
          send to first token rather than an indicator that blinks out between
          stages. It gives way the moment there is real text to show. */}
      {pending && !message.text && <Thinking />}

      {message.text && (
        <div className="answer max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-line px-4 py-3 text-sm text-ink">
          {/* react-markdown builds a React element tree — no dangerouslySetInnerHTML,
              so raw HTML in the model output is inert unless rehype-raw is added (it
              isn't). remark-gfm adds tables, task lists, strikethrough and autolinks. */}
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {message.text}
          </ReactMarkdown>
        </div>
      )}

      {message.blocked && message.blocked.length > 0 && (
        <p className="text-xs text-gold-ink bg-gold-tint border border-gold/40 rounded-md px-3 py-2 max-w-[85%]">
          Blocked by your role: {message.blocked.join(", ")}
        </p>
      )}

      {message.error && (
        <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 max-w-[85%]">
          {message.error}
        </p>
      )}
    </div>
  );
}
